import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { RadioGroup } from '@/components/ui/RadioGroup';
import { SettingRow } from '@/components/ui/Field';
import { Switch } from '@/components/ui/Switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { LandingPagePicker } from '@/components/settings/LandingPagePicker';
import { useSettingsPatch, useUserPrefs } from '@/api/bi/settings';
import { storage, type UserPrefs } from '@/lib/storage';
import { SavingIndicator } from './SavingIndicator';

/**
 * Workspace defaults — where you start, what you compare against, how often
 * data refreshes.
 *
 * Grouped by when the setting takes effect, because "applies on next reload"
 * used to be a single sentence at the bottom covering some of the controls
 * above it with no way to tell which ones.
 */
export function PreferencesPage() {
  const prefs = useUserPrefs();
  const patchSettings = useSettingsPatch();

  function patch(next: Partial<UserPrefs>) {
    storage.setPrefs(next);
    // Send the whole merged prefs object rather than the slice: a shallow merge
    // on either side would otherwise drop siblings like `landing_path` when
    // only `default_range` changed.
    patchSettings({ prefs: storage.getPrefs() });
  }

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Workspace"
        title="Preferences"
        description="Defaults applied when a page has nothing more specific to go on. Saved automatically."
        actions={<SavingIndicator />}
      />

      <Card>
        <CardHeader>
          <CardTitle>Where you start</CardTitle>
        </CardHeader>
        <CardBody className="space-y-2.5">
          <SettingRow
            label="Landing page"
            hint="Opened on a cold boot. Only pages you have access to are listed, and deep links always win."
          >
            <LandingPagePicker value={prefs.landing_path} onChange={(v) => patch({ landing_path: v })} />
          </SettingRow>

          <SettingRow
            label="Show the Overview welcome card"
            hint="Hide the greeting strip to land straight in the data."
          >
            <Switch
              checked={prefs.show_welcome}
              onCheckedChange={(v) => patch({ show_welcome: v })}
              aria-label="Show the Overview welcome card"
            />
          </SettingRow>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Default filters</CardTitle>
          <span className="text-[11px] text-muted-foreground">Applied on the next page load</span>
        </CardHeader>
        <CardBody className="space-y-2.5">
          <SettingRow label="Date range" hint="Used when the URL carries no range parameter.">
            <Select
              value={prefs.default_range ?? '7d'}
              onValueChange={(v) => patch({ default_range: v as UserPrefs['default_range'] })}
            >
              <SelectTrigger className="h-8 w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="this-week">This week (Sun–Sat)</SelectItem>
                <SelectItem value="last-week">Last week (Sun–Sat)</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="mtd">Month to date</SelectItem>
              </SelectContent>
            </Select>
          </SettingRow>

          <SettingRow label="Compare against" hint="The baseline every delta is measured from.">
            <Select
              value={prefs.default_compare ?? 'previous-period'}
              onValueChange={(v) => {
                const mode = v as UserPrefs['default_compare'];
                patch({ default_compare: mode });
                // Also move the live compare mode so the Topbar reflects the
                // choice now instead of only after a reload.
                if (mode) {
                  storage.setCompareMode(mode);
                  patchSettings({ compare_mode: mode });
                }
              }}
            >
              <SelectTrigger className="h-8 w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="previous-period">Previous period</SelectItem>
                <SelectItem value="previous-year">Previous year</SelectItem>
                <SelectItem value="none">Off</SelectItem>
              </SelectContent>
            </Select>
          </SettingRow>

          <SettingRow
            label="Day boundary"
            hint="Business days run to the operational cut-off rather than midnight."
          >
            <Select
              value={prefs.time_range_mode}
              onValueChange={(v) => patch({ time_range_mode: v as UserPrefs['time_range_mode'] })}
            >
              <SelectTrigger className="h-8 w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (midnight)</SelectItem>
                <SelectItem value="business">Business day</SelectItem>
              </SelectContent>
            </Select>
          </SettingRow>

          <SettingRow
            label="Refresh cadence"
            hint="How often Live Ops and Overview re-poll. Off means manual refresh only."
          >
            <Select value={String(prefs.refresh_ms)} onValueChange={(v) => patch({ refresh_ms: Number(v) })}>
              <SelectTrigger className="h-8 w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Off</SelectItem>
                <SelectItem value="15000">Every 15s</SelectItem>
                <SelectItem value="30000">Every 30s</SelectItem>
                <SelectItem value="60000">Every minute</SelectItem>
                <SelectItem value="180000">Every 3 minutes</SelectItem>
                <SelectItem value="300000">Every 5 minutes</SelectItem>
              </SelectContent>
            </Select>
          </SettingRow>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Numbers &amp; explanations</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <RadioGroup
            ariaLabel="Big number format"
            value={prefs.kpi_format ?? 'compact'}
            onChange={(v) => patch({ kpi_format: v })}
            options={[
              { value: 'compact', label: 'Compact — ₹1.2M', hint: 'Reads better on wall displays and boards.' },
              { value: 'exact', label: 'Exact — ₹1,234,567', hint: 'What finance needs when the digits matter.' },
            ]}
          />
          <SettingRow
            label="“Why?” popovers on KPIs"
            hint="Adds a button to each KPI that breaks down top contributors and movers."
          >
            <Switch
              checked={prefs.why_popovers !== false}
              onCheckedChange={(v) => patch({ why_popovers: v })}
              aria-label="Why popovers on KPIs"
            />
          </SettingRow>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alerts</CardTitle>
        </CardHeader>
        <CardBody>
          <SettingRow
            label="Share alerts with the team"
            hint="Mirror alerts you trigger to the team inbox so someone else can acknowledge them."
          >
            <Switch
              checked={prefs.share_alerts_with_team !== false}
              onCheckedChange={(v) => patch({ share_alerts_with_team: v })}
              aria-label="Share alerts with the team"
            />
          </SettingRow>
        </CardBody>
      </Card>
    </div>
  );
}
